import type { QuotationSupplierProduct } from 'apis/restaurant-inquiries';
import { useGetQuotationSupplierProductsApi, useGetSuggestedQuotationProductsApi } from 'apis/restaurant-inquiries';
import { useMemo } from 'react';
import {
  rankCatalogSuggestions,
  RankedSuggestion,
  searchProbe,
  SuggestionRequestItem,
  SUGGESTION_MATCH_THRESHOLD,
} from './rank-catalog-suggestions';

// Mirrors the fixed page size in useGetQuotationSupplierProductsApi.
const PAGE_LIMIT = 50;

// Suggestions for one item card, BE-first: the suggested-products endpoint
// scores the WHOLE catalog server-side against the item's master product +
// flexibility (matchScore >= 95, exact-tier only, max 6). While the BE isn't
// deployed yet it 404s, and the legacy client-side path takes over — page 1 of
// the catalog plus ONE extra probe by the most distinctive request token when
// the catalog overflows page 1 (react-query dedupes identical probes across
// item cards in the drawer), merged by product id and ranked to the same
// exact-tier/flexibility contract.
export function useSupplierCatalogSuggestions(
  supplierId: number | null | undefined,
  item: SuggestionRequestItem | undefined
): { suggestions: RankedSuggestion[]; isLoading: boolean } {
  const suggested = useGetSuggestedQuotationProductsApi(supplierId, item?.id);
  const useFallback = suggested.isError && suggested.error?.response?.status === 404;

  const base = useGetQuotationSupplierProductsApi(supplierId, undefined, { enabled: !!supplierId && useFallback });
  const probe = searchProbe(item?.masterProduct?.name);
  const needsProbe = (base.data?.data?.length ?? 0) >= PAGE_LIMIT;
  const probed = useGetQuotationSupplierProductsApi(supplierId, probe, {
    enabled: !!supplierId && useFallback && !!probe && needsProbe,
  });

  const fallbackCatalog = useMemo(() => {
    const byId = new Map<number, QuotationSupplierProduct>();
    (base.data?.data ?? []).forEach((p) => byId.set(p.id, p));
    (probed.data?.data ?? []).forEach((p) => byId.set(p.id, p));
    return Array.from(byId.values());
  }, [base.data, probed.data]);

  const suggestions = useMemo(() => {
    if (!useFallback) {
      return (suggested.data?.data ?? [])
        .filter((p) => (p.matchScore ?? 0) >= SUGGESTION_MATCH_THRESHOLD)
        .map((p) => ({ product: p, matchScore: p.matchScore ?? SUGGESTION_MATCH_THRESHOLD }));
    }
    return rankCatalogSuggestions(fallbackCatalog, item);
  }, [useFallback, suggested.data, fallbackCatalog, item]);

  const isLoading = useFallback
    ? base.isLoading || (needsProbe && probed.isLoading)
    : suggested.isLoading || suggested.isFetching;

  return { suggestions, isLoading };
}
